import React, {useContext, useEffect, useState} from "react";
import Markup from "../../../markup";
import useExporter from "../../../builder/hooks/useExporter";
import BuilderContext from "../../../builder/hooks/context";
import { device } from '../../../const/device';
import {useMediaQuery} from "react-responsive";
import { trackGoToCart, trackSaveState } from "../../../utils/Analytics";
import { useTimeTracking } from "../../../utils/TimeTrackingContext";

const SaveBuilderComponent = ({Child}) => {
    const { builder } = useContext(BuilderContext);
    const { logTimeSpent } = useTimeTracking();
    const exporter = useExporter(builder);
    const isTablet = useMediaQuery({ query: `(${device.tablet})` });

    const [sendBtnState, setSendBtnState] = useState<boolean>(false);
    const [errorState, setErrorState] = useState<string>("");

    useEffect(() => {
        setSendBtnState(Boolean(builder.imagesData.activeUploadsCounter))
    },[builder.imagesData.activeUploadsCounter])

    const builderMakeRequest = (action) => {
        const onSuccess = () => {
            builder.setSaveBuilderState(false);
            setSendBtnState(false);
        }
        const onError = (message:string|undefined = "An error occured, please try again later") => {
            setErrorState(message);
            builder.setSaveBuilderState(false);
            setTimeout(() => {
                setSendBtnState(false);
                setErrorState("");
            }, 5000);
        }

        const exporterAction = async (data, onSuccess, onError)  => {
            try {
                action(data, onSuccess, onError);
                if(action.name === "onSave") builder.setSaveBuilderState(false);
            }catch(e){
                onError(e);
            }
        }

        builder.setSaveBuilderState(true);
        if(action.name === "onSubmit") {
            // Validate that all members have a size selected
            const membersWithoutSize = builder.membersData.members.filter(
                (member) => !member.data.size || member.data.size === ''
            );
            if (membersWithoutSize.length > 0) {
                builder.setSaveBuilderState(false);
                const memberCount = builder.membersData.members.length;
                if (memberCount === 1) {
                    setErrorState("PLEASE SELECT A SIZE BEFORE ADDING TO CART");
                } else {
                    // Find which member numbers are missing sizes
                    const missingIndices = builder.membersData.members
                        .map((member, i) => (!member.data.size || member.data.size === '') ? i + 1 : null)
                        .filter(i => i !== null);
                    setErrorState(`PLEASE SELECT A SIZE FOR MEMBER${missingIndices.length > 1 ? 'S' : ''} ${missingIndices.join(', ')} BEFORE ADDING TO CART`);
                }
                setTimeout(() => {
                    setErrorState("");
                }, 5000);
                return;
            }

            const hasBridgeFunctions =
              typeof (window as any).builderSaveDesign === 'function' &&
              typeof (window as any).builderAddToCartBatch === 'function';

            if (hasBridgeFunctions) {
                // Batch flow — render all members, save design once, add all to cart, redirect
                builder.setSaveBuilderState(true);
                setSendBtnState(true);
                exporter(
                    (data, ok, err) => {}, // dummy action — exporter uses bridge functions directly
                    () => {
                        builder.setSaveBuilderState(false);
                        setSendBtnState(false);
                        window['builderOnCart']();
                    },
                    (msg) => {
                        setErrorState(msg || "An error occurred, please try again later");
                        builder.setSaveBuilderState(false);
                        setTimeout(() => { setSendBtnState(false); setErrorState(""); }, 5000);
                    },
                    'onBatchCart'
                );
            } else {
                // Legacy: require per-member add-to-cart before redirecting
                builder.setSaveBuilderState(false);
                let compareAr = [];
                builder.membersData.members.map((member) => {
                    if(!member.resultAddedCart){
                        builder.firstUpdateShCompMessage(true);
                        builder.updateShowCompMessage(true);
                        (isTablet) && builder.updateMobShowCompMessage(true);
                    }else{
                        let bl = true;
                        compareAr.push(bl);
                    }
                })
                if(compareAr.length === builder.membersData.members.length){
                    builder.setSaveBuilderState(true);
                    window['builderOnCart']();
                }
            }
        }else{
            exporter( exporterAction, onSuccess, onError, action.name);
        }
    }

    const onSave = (data, onSuccess, onError) => {
        window['builderOnSave'](data, onSuccess, onError);
        trackSaveState();
    }

    const onSubmit = (data, onSuccess, onError) => {
        //setSendBtnState(true);
        //window['builderOnSubmit'](data, onSuccess, onError);
        //window['onRedirectCart']();
        trackGoToCart('button');
        logTimeSpent();
    }

    const themeColor = () => {
        return builder.membersData.colorTheme;
    }

    return(
        <>
            {errorState && <Markup.Global.Notification.ErrorNotification>{errorState}</Markup.Global.Notification.ErrorNotification>}
            <Child sendBtnState={sendBtnState} builderMakeRequest={builderMakeRequest} onSave={onSave} onSubmit={onSubmit} themeColor={themeColor}/>
        </>
    )
};

const Buttons = ({sendBtnState, builderMakeRequest, onSave, onSubmit}) => {
    const { builder } = useContext(BuilderContext);
    const isEditMode = sessionStorage.getItem('builder_action') === 'update';
    return <Markup.Screen.Name.ButtonContainer>
        <Markup.Screen.Name.SaveButton loading={sendBtnState?.toString()} onClick={() => builderMakeRequest(onSave)} themeColor={builder.membersData.colorTheme}>
            SAVE BUILD
        </Markup.Screen.Name.SaveButton>
        <Markup.Screen.Name.ButtonLinkCart onClick={() => builderMakeRequest(onSubmit)}>
            {isEditMode ? 'UPDATE CART' : 'ADD TO CART'}
        </Markup.Screen.Name.ButtonLinkCart>
    </Markup.Screen.Name.ButtonContainer>
}

const Icon = ({builderMakeRequest, onSave, sendBtnState}) => {
    const { builder } = useContext(BuilderContext);
    return (<Markup.Global.Link.LinkSaveIconMobile loading={sendBtnState?.toString()} onClick={() => builderMakeRequest(onSave)} themeColor={builder.membersData.colorTheme} />)
}

const MobileIcon = ({builderMakeRequest, onSave, sendBtnState}) => {
    return (<Markup.Global.Link.LinkSaveIconMobile loading={sendBtnState?.toString()} onClick={() => builderMakeRequest(onSave)} themeColor={'white'} children={<div>Save Build</div>}/>)
}

export const SaveButtons = () => {
    return <SaveBuilderComponent Child={Buttons}/>
}

export const SaveIcon = () => {
    return <SaveBuilderComponent Child={Icon}/>
}

export const SaveIconMobile = () => {
    return <SaveBuilderComponent Child={MobileIcon}/>
}